import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "@/lib/api";
import { CheckCircle2, Package, Truck, MapPin, Home, AlertTriangle } from "lucide-react";
import Seo from "@/components/Seo";

export default function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.order(orderId);
        setOrder(data);
      } catch (e) { setErr(e.message || "Order not found"); }
    })();
  }, [orderId]);

  if (err) return <div className="mx-auto max-w-2xl px-4 py-24 text-center text-[var(--yp-muted)]">{err}</div>;
  if (!order) return <div className="mx-auto max-w-2xl px-4 py-24 text-center text-[var(--yp-muted)]">Loading order…</div>;

  const stages = order.stages;
  const currentStep = order.current_stage;
  // An exception status (Cancelled/Delivery Failed/Returned) is never one of the normal pipeline
  // stages, so this is enough to detect one without the backend needing to say so explicitly —
  // current_stage stays frozen at wherever the order actually got to before it happened.
  const isException = !stages.includes(order.status);

  return (
    <div data-testid="order-tracking-page" className="mx-auto max-w-4xl px-4 py-10 md:py-14">
      <Seo title="Order Tracking" path={`/order/${orderId}`} noindex={true} />
      <div className="text-center bg-[var(--yp-surface-alt)] rounded-3xl p-8 border border-[var(--yp-border)]">
        <CheckCircle2 size={40} className="mx-auto text-[var(--yp-success)]" />
        <h1 className="font-serif-display text-3xl text-[var(--yp-primary)] mt-3">Thank you — your order is confirmed</h1>
        <div className="text-sm text-[var(--yp-muted)] mt-1">Order ID <span data-testid="order-id" className="font-semibold text-[var(--yp-text)]">{order.id}</span></div>
        <div className="text-xs text-[var(--yp-muted)] mt-1">Expected delivery: {order.estimated_delivery}</div>
      </div>

      {isException && (
        <div className="mt-6 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-4 text-sm">
          <AlertTriangle size={18} className="shrink-0" />
          <span>This order was <strong>{order.status}</strong>. Contact support if you have questions.</span>
        </div>
      )}

      {/* Timeline */}
      <div className="mt-10 bg-white border border-[var(--yp-border)] rounded-2xl p-6">
        <div className="font-serif-display text-xl text-[var(--yp-primary)]">Order status</div>
        <div className="mt-6 space-y-4">
          {stages.map((s, i) => {
            const done = i <= currentStep;
            const Icon = i === stages.length - 1 ? Home : i >= stages.length - 3 ? Truck : i >= 2 ? Package : CheckCircle2;
            return (
              <div key={s} className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full grid place-items-center ${done ? "bg-[var(--yp-primary)] text-white" : "bg-[var(--yp-surface-alt)] text-[var(--yp-muted)]"}`}>
                  <Icon size={16} />
                </div>
                <div className={`flex-1 text-sm ${done ? "font-medium text-[var(--yp-text)]" : "text-[var(--yp-muted)]"}`}>{s}</div>
                {i === currentStep && <span className="chip !text-[var(--yp-primary)]">Current</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Items */}
      <div className="mt-8 grid md:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white border border-[var(--yp-border)] rounded-2xl p-6">
          <div className="font-serif-display text-xl text-[var(--yp-primary)]">Items</div>
          <div className="mt-4 divide-y divide-[var(--yp-border)]">
            {order.items.map((it, i) => (
              <div key={i} className="py-3 flex gap-3 text-sm">
                {it.image && <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover" />}
                <div className="flex-1">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs text-[var(--yp-muted)]">{it.size ? `${it.size} · ` : ""}Qty {it.quantity}</div>
                </div>
                <div className="font-medium">{inr(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 leaf-divider" />
          <div className="mt-3 text-sm space-y-2">
            <Row label="Subtotal" value={inr(order.subtotal)} />
            <Row label="Shipping" value={inr(order.shipping)} />
            <Row label="Discount" value={inr(order.discount)} />
            <Row label={<span className="font-semibold text-[var(--yp-primary)]">Total (COD)</span>} value={<span className="font-semibold text-[var(--yp-primary)]">{inr(order.total)}</span>} />
          </div>
        </div>
        <div className="bg-white border border-[var(--yp-border)] rounded-2xl p-6">
          <div className="font-serif-display text-xl text-[var(--yp-primary)]">Delivery address</div>
          <div className="text-sm mt-3 leading-relaxed">
            <div className="font-medium">{order.address.full_name}</div>
            <div>{order.address.line1}</div>
            <div>{order.address.city}, {order.address.state} - {order.address.pincode}</div>
            <div className="mt-2 text-[var(--yp-muted)] flex items-center gap-1"><MapPin size={14} /> {order.address.phone}</div>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link to="/shop" className="btn-primary rounded-full px-6 py-3 text-sm font-medium">Continue shopping</Link>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><div>{label}</div><div>{value}</div></div>;
}
